import { useCurrentWorkspace } from '@gripp/shared-logic';
import {
  SlideAction,
  SlideLayout,
  Spacing,
  ViewMode,
  type ColumnData,
  type SlideLayoutLocationState,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ActivityEditLabels from '../../activity/components/activity-edit-label';
import { NewIssueBody } from '../../issues';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

const ASSET_NEW_ISSUE_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetNewIssue.tsx';
const ASSET_SUB_ROUTES_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/assetSubRoutes.tsx';
const ASSET_CARD_SOURCE =
  'gripp-workspace/apps/gripp-web/src/assets/components/assetCard.tsx';
const ASSET_ROUTE_PATTERN = '/assets/:assetId/activity/new';
const ACTIVITY_ASSET_ROUTE_PATTERN =
  '/activities/:issueId/asset/:assetId/activity/new';
const ASSET_ACTIVITY_ROUTE_PANEL_KEY = 'asset-activity-new';
const ASSET_CARD_PANEL_DEBUG_ID = 'asset-card';

export const AssetNewIssue = () => {
  const { assetId = '', issueId: contextIssueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);
  const [selectedLabels, setSelectedLabels] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [isLabelsPanelOpen, setIsLabelsPanelOpen] = useState(false);
  const currentWorkspace = useCurrentWorkspace();
  const resetLabelsRef = useRef<(() => void) | undefined>(undefined);
  const routePattern = contextIssueId
    ? ACTIVITY_ASSET_ROUTE_PATTERN
    : ASSET_ROUTE_PATTERN;
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  const handleIssueCreated = (newIssueId: string) => {
    if (contextIssueId) {
      // Activities context — navigate to the new issue in activities.
      navigate(`/activities/${newIssueId}`);
    } else {
      // Assets context — navigate to the new issue within the asset.
      navigate(`${basePath}/activity/${newIssueId}`);
    }
  };

  const handleAssetCardBackPress = () => {
    navigate(contextIssueId ? `/activities/${contextIssueId}` : '/assets', {
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });
  };

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    mainComponent: <AssetCard assetId={assetId} />,
    headerComponent: <AssetFilters assetId={assetId} />,
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonTransitionExitColumn: 'both',
    onBackButtonPress: handleAssetCardBackPress,
    debug: {
      id: ASSET_CARD_PANEL_DEBUG_ID,
      titleKey: 'assets.menu.title',
      component: 'AssetCard',
      componentSource: ASSET_CARD_SOURCE,
      columnSource: ASSET_NEW_ISSUE_SOURCE,
      ownerComponent: 'AssetNewIssue',
      ownerSource: ASSET_NEW_ISSUE_SOURCE,
      routePanelKey: ASSET_ACTIVITY_ROUTE_PANEL_KEY,
      routePattern,
      routeSource: ASSET_SUB_ROUTES_SOURCE,
      source: {
        owner: {
          file: ASSET_NEW_ISSUE_SOURCE,
          line: 32,
          symbol: 'AssetNewIssue',
        },
        column: {
          file: ASSET_NEW_ISSUE_SOURCE,
          line: 69,
          symbol: 'leftColumn',
        },
        component: {
          file: ASSET_CARD_SOURCE,
          line: 30,
          symbol: 'AssetCard',
        },
        route: {
          file: ASSET_SUB_ROUTES_SOURCE,
          line: 48,
          symbol: 'assetSubRoutes',
        },
      },
      entity: { type: 'asset', id: assetId },
    },
  };

  const rightColumn: ColumnData = {
    title: 'issue.new.addIt',
    mainComponent: (
      <NewIssueBody
        assetId={assetId}
        workspaceId={currentWorkspace?.id}
        isOpened={true}
        modalType="sheet"
        onComplete={() =>
          navigate('../..', {
            relative: 'path',
            state: { slideLayoutInitialTransition: SlideAction.PREV },
          })
        }
        onCancel={() =>
          navigate('../..', {
            relative: 'path',
            state: { slideLayoutInitialTransition: SlideAction.PREV },
          })
        }
        onIssueCreated={handleIssueCreated}
        selectedLabels={selectedLabels}
        onLabelsChange={setSelectedLabels}
        onSelectorEdit={() => setIsLabelsPanelOpen(true)}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    onBackButtonPress: () =>
      navigate('../..', {
        relative: 'path',
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      }),
    disableScroll: true,
    debug: {
      id: 'asset-new-issue:right-new-issue-body',
      titleKey: 'issue.new.addIt',
      component: 'NewIssueBody',
      componentSource:
        'gripp-workspace/apps/gripp-web/src/issues/components/newIssueModal.tsx',
      columnSource: ASSET_NEW_ISSUE_SOURCE,
      ownerComponent: 'AssetNewIssue',
      ownerSource: ASSET_NEW_ISSUE_SOURCE,
      routePanelKey: ASSET_ACTIVITY_ROUTE_PANEL_KEY,
      routePattern,
      routeSource: ASSET_SUB_ROUTES_SOURCE,
      source: {
        owner: {
          file: ASSET_NEW_ISSUE_SOURCE,
          line: 32,
          symbol: 'AssetNewIssue',
        },
        column: {
          file: ASSET_NEW_ISSUE_SOURCE,
          line: 115,
          symbol: 'rightColumn',
        },
        component: {
          file: 'gripp-workspace/apps/gripp-web/src/issues/components/newIssueModal.tsx',
          line: 76,
          symbol: 'NewIssueBody',
        },
        route: {
          file: ASSET_SUB_ROUTES_SOURCE,
          line: 48,
          symbol: 'assetSubRoutes',
        },
      },
      entity: { type: 'activityDraft', id: contextIssueId },
    },
  };

  const thirdColumn: ColumnData | undefined = isLabelsPanelOpen
    ? {
        title: 'asset.groups',
        mainComponent: (
          <ActivityEditLabels
            selectedLabels={selectedLabels ?? []}
            onLabelsChange={setSelectedLabels}
            resetRef={resetLabelsRef}
          />
        ),
        headerComponent: (
          <View style={styles.headerResetButton}>
            <HeaderResetButton onReset={() => resetLabelsRef.current?.()} />
          </View>
        ),
        showBackButton: true,
        disableScroll: true,
        onBackButtonPress: () => setIsLabelsPanelOpen(false),
        debug: {
          id: 'asset-new-issue:third-activity-edit-labels',
          titleKey: 'asset.groups',
          component: 'ActivityEditLabels',
          componentSource:
            'gripp-workspace/apps/gripp-web/src/activity/components/activity-edit-label.tsx',
          columnSource: ASSET_NEW_ISSUE_SOURCE,
          ownerComponent: 'AssetNewIssue',
          ownerSource: ASSET_NEW_ISSUE_SOURCE,
          routePanelKey: ASSET_ACTIVITY_ROUTE_PANEL_KEY,
          routePattern,
          routeSource: ASSET_SUB_ROUTES_SOURCE,
          source: {
            owner: {
              file: ASSET_NEW_ISSUE_SOURCE,
              line: 32,
              symbol: 'AssetNewIssue',
            },
            column: {
              file: ASSET_NEW_ISSUE_SOURCE,
              line: 188,
              symbol: 'thirdColumn',
            },
            component: {
              file: 'gripp-workspace/apps/gripp-web/src/activity/components/activity-edit-label.tsx',
              line: 17,
              symbol: 'ActivityEditLabels',
            },
            route: {
              file: ASSET_SUB_ROUTES_SOURCE,
              line: 48,
              symbol: 'assetSubRoutes',
            },
          },
          entity: { type: 'activityLabels', id: contextIssueId },
        },
      }
    : undefined;

  return (
    <AssetRouteProvider
      value={{
        basePath: contextIssueId
          ? `/activities/${contextIssueId}/asset/${assetId}`
          : `/assets/${assetId}`,
        listPath: contextIssueId ? '/activities' : '/assets',
        isWorkspaceMode: false,
        issueId: contextIssueId,
      }}
    >
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        thirdColumn={thirdColumn}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
    </AssetRouteProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
  },
  headerResetButton: {
    marginRight: 8,
  },
});
